/**
 * Store Pinia des stocks (composition API)
 */
import { defineStore } from "pinia";
import { ref, computed } from "vue";
import StockService from "../services/StockService";
import { IStock } from "../models/stock";
import { IStockForm } from "../models/forms/stockForm";
import { IStockApiResponse } from "../models/api/stockApiResponse";
import { IStockFilter } from "../models/filters/stockFilter";
import { ServiceOperationResult } from "../models/api/serviceOperationResult";
import { IDataOperationResult } from "../models/api/dataOperationResult";
import DataOperationError from "../models/api/dataOperationError";
import { ICenter } from "../models/center";
import { IStockUnity } from "../models/stockUnity";
import { isNumeric } from "../helper/stringHelper";
import { GroupedStock } from "../helper/type";
import { useCenterStore } from "./center.store";
import { useStockUnityStore } from "./stockUnity.store";
import signalRManager from "../helper/signalRManager";
import IPayloadStore from "../models/api/payloadStore";

export const useStockStore = defineStore("stocks", () => {
    // State
    const datas = ref<IStock[]>([]);
    const current = ref<IStock>({} as IStock);
    const itemForm = ref<IStockForm>({} as IStockForm);
    const isLoading = ref<boolean>(false);

    // Getters
    const getListeStockSemence = computed(() => (bFilterByCodeSchema: boolean = false) => {
        const filteredStocks = datas.value.filter(
            (stock) => !isNumeric(stock.unity.article?.code) && stock.unity.article.code
        );

        const groupedStocks = filteredStocks.reduce((acc: { [key: string]: GroupedStock }, stock) => {
            const key = bFilterByCodeSchema
                ? `${stock.unity.isArmada}-${stock.unity.heterospermie}-${stock.unity.conditionnement?.value}-${stock.unity?.article?.geneticType?.code3}-${stock.unity?.volumeDose}-${stock.unity?.spzDose}`
                : `${stock.unity.article.code}-${stock.unity.isArmada}-${stock.unity.heterospermie}-${stock.unity.conditionnement?.value}-${stock.unity?.article?.geneticType?.code3}-${stock.unity?.volumeDose}-${stock.unity?.spzDose}`;

            if (!acc[key]) {
                acc[key] = {
                    codeArticle: stock.unity.article.code,
                    idArticle: stock.unity.article.id,
                    libelleArticle: stock.unity.article.libelle,
                    centerCode: stock.center?.code,
                    isArmada: stock.unity.isArmada ?? false,
                    isHeterospermie: Boolean(stock.unity.heterospermie),
                    conditionnement: stock.unity.conditionnement?.value,
                    conditionnementCode: stock.unity.conditionnement?.code3,
                    schemaCode: stock.unity?.article?.geneticType?.code3,
                    volumeDose: stock.unity.volumeDose,
                    spzDose: stock.unity.spzDose,
                    stockUnity: stock.unity,
                    quantity: 0,
                    stocks: [],
                    isPrevisional: false,
                };
            }
            acc[key].quantity += stock.quantity;
            acc[key].stocks.push(stock);
            return acc;
        }, {});

        return Object.values(groupedStocks);
    });

    // Actions - Hydration
    /**
     * Hydrate une liste de stocks en batch (optimisé pour la performance)
     * - Charge centerStore et stockUnityStore si nécessaires
     * - Crée des Maps d'index pour des lookups O(1)
     */
    async function hydrateStocks(items: IStockApiResponse[]): Promise<IStock[]> {
        if (items.length === 0) return [];

        // 1. Récupérer les stores dépendants
        const centerStore = useCenterStore();
        const stockUnityStore = useStockUnityStore();

        // 2. Charger les stores dépendants en parallèle si nécessaire
        const loadPromises: Promise<unknown>[] = [];

        if (centerStore.datas.length === 0) {
            loadPromises.push(centerStore.load({ page: 1, pageSize: 0 }));
        }
        if (stockUnityStore.datas.length === 0) {
            loadPromises.push(stockUnityStore.load({ page: 1, pageSize: 0 }));
        }

        if (loadPromises.length > 0) {
            await Promise.all(loadPromises);
        }

        // 3. Créer des Maps pour des lookups O(1)
        const centerMap = new Map(centerStore.datas.map(c => [c.id, c]));
        const stockUnityMap = new Map(stockUnityStore.datas.map(u => [u.id, u]));

        // 4. Hydrater tous les stocks en une seule passe
        const models: IStock[] = [];
        for (const item of items) {
            const model = { ...item } as unknown as IStock;

            // Lier les références (lookup O(1) via Map)
            model.center = centerMap.get(item.centerId)!;
            model.unity = stockUnityMap.get(item.unityId)!;

            models.push(model);
        }

        return models;
    }

    // Actions

    async function resetStock(centerCodeSpecies: string) {
        isLoading.value = true;
        const result = await StockService.resetStock(centerCodeSpecies);
        isLoading.value = false;
        datas.value = [];
        return result;
    }

    function getStock(schemaCode: string, codeConditionnement: string, armada: boolean, heterospermie: boolean, concentrationDose: number, volume: number) {
        const specificStocks = getListeStockSemence.value();

        const listSpecificStocks = specificStocks.filter(
            (group) =>
                group.codeArticle.startsWith(schemaCode) &&
                group.conditionnementCode === codeConditionnement &&
                group.isHeterospermie === heterospermie &&
                group.isArmada === armada &&
                (Math.ceil(((group.spzDose ?? 0) / (group.volumeDose ?? 1))) >= concentrationDose) &&
                group.volumeDose == volume
        );

        var totalQuantity = listSpecificStocks.reduce((sum, group) => sum -= group.quantity, 0);

        return {
            total: totalQuantity,
            stocks: listSpecificStocks,
        };
    }

    function getStockHeterospermie(schemaCode: string, armada: boolean, volume: number, concentrationDose: number) {
        const specificStocks = getListeStockSemence.value();

        const listSpecificStocks = specificStocks.filter(
            (group) =>
                group.codeArticle.startsWith(schemaCode) &&
                group.conditionnementCode != 'H' &&
                group.isHeterospermie == true &&
                (Math.ceil(((group.spzDose ?? 0) / (group.volumeDose ?? 1))) >= concentrationDose) &&
                group.isArmada === armada &&
                group.volumeDose == volume
        );

        var totalQuantity = listSpecificStocks.reduce((sum, group) => sum -= group.quantity, 0);

        return {
            total: totalQuantity,
            stocks: listSpecificStocks,
        };
    }

    function getSpecificStock(schemaCode: string, codeConditionnement: string, armada: boolean, heterospermie: boolean, codearticle: string, concentrationDose: number, volume: number, isStandard: boolean, isArticleCheck: boolean, hasOtherOtherOrder: boolean) {
        const specificStocks = getListeStockSemence.value();

        const listSpecificStocks = specificStocks.filter(
            (group) =>
                group.codeArticle.startsWith(schemaCode) &&
                group.conditionnementCode === codeConditionnement &&
                group.isArmada === armada &&
                group.isHeterospermie === heterospermie &&
                (Math.ceil(((group.spzDose ?? 0) / (group.volumeDose ?? 1))) >= concentrationDose) &&
                (!isArticleCheck || group.codeArticle === codearticle)
        );

        var totalQuantity = listSpecificStocks.reduce((sum, group) => sum -= group.quantity, 0);

        return {
            total: totalQuantity,
            stocks: listSpecificStocks,
        };
    }

    /**
     * Chargement des données dans le store
     */
    async function load(filter: IStockFilter) {
        isLoading.value = true;
        const result = await StockService.getAll<IStockFilter, IStockApiResponse>(filter);
        isLoading.value = false;
        datas.value = [];

        if (!result.error) {
            datas.value = await hydrateStocks(result.data as IStockApiResponse[]);
        }

        return result;
    }

    /**
     * Récupération d'un stock par son identifiant
     */
    async function get(id: number) {
        const result = await StockService.getById<IStockApiResponse>(id);

        if (!result.error) {
            const [hydratedItem] = await hydrateStocks([result.data as IStockApiResponse]);

            const index = datas.value.findIndex((a) => a.id == id);
            if (index >= 0)
                datas.value[index] = hydratedItem;

            current.value = hydratedItem;
            itemForm.value = toForm(hydratedItem);
        }

        return result;
    }

    /**
     * Récupération de l'objet dans le store
     */
    async function searchStoreById(id: number): Promise<IStock> {
        current.value = datas.value.find((objet) => objet.id == id)!;
        itemForm.value = toForm(current.value as IStock);
        return current.value;
    }

    /**
     * Mise à jour ou création d'un objet
     */
    async function save(maj: boolean = true, isPatch: boolean = false) {
        let result = {} as ServiceOperationResult<IStock | IDataOperationResult<IStock>>;

        if (itemForm.value.id > 0) {
            result = await StockService.update(itemForm.value);

            if (!result.error) {
                const [hydratedItem] = await hydrateStocks([result.data as unknown as IStockApiResponse]);
                current.value = hydratedItem;

                if (maj) {
                    const index = datas.value.findIndex((a) => a.id == itemForm.value.id);
                    if (index >= 0) {
                        datas.value[index] = current.value;
                    } else {
                        const error = new DataOperationError();
                        error.REQUEST_ERROR = "Item non trouvé dans le store";
                        return new ServiceOperationResult<IDataOperationResult<IStock>>(true, { errors: error });
                    }
                }
            }
        } else {
            result = await StockService.create(itemForm.value);

            if (!result.error) {
                const [hydratedItem] = await hydrateStocks([result.data as unknown as IStockApiResponse]);
                current.value = hydratedItem;
                if (datas.value.findIndex((a) => a.id == current.value?.id) == -1) {
                    datas.value.push(current.value);
                }
            }
        }

        return result;
    }

    /**
     * Delete de l'objet
     */
    async function deleteAction() {
        const result = await StockService.delete<IStockApiResponse>(current.value.id);

        if (!result.error) {
            const index = datas.value.findIndex((a) => a.id == current.value.id);
            if (index >= 0) {
                datas.value.splice(index, 1);
            }

            current.value = newItem({ id: 0, name: "", code: "", isActive: true } as ICenter);
            itemForm.value = newForm(0);
        }

        return result;
    }

    /**
     * Transformation de l'objet en formulaire
     */
    function toForm(item: IStock): IStockForm {
        const form = {} as IStockForm;
        form.id = item.id;
        form.code = item.code;
        form.centerId = item.center.id;
        form.quantity = item.quantity;
        form.unityId = item.unity.id;
        itemForm.value = form;
        return form;
    }

    /**
     * Permet d'initialiser un objet vide pour le formulaire
     */
    function newForm(lienId: number): IStockForm {
        itemForm.value = {
            id: 0,
            centerId: lienId,
            code: "",
            quantity: 0,
            unityId: 0
        } as IStockForm;
        return itemForm.value;
    }

    /**
     * Permet d'initialiser un objet vide pour les referentiels
     */
    function newItem(lien: ICenter): IStock {
        current.value = {
            id: 0,
            code: "",
            quantity: 0,
            center: lien,
            unity: { id: 0 } as IStockUnity,
        } as IStock;
        itemForm.value = toForm(current.value);
        return current.value;
    }

    return {
        // State
        datas,
        current,
        itemForm,
        isLoading,
        // Getters
        getListeStockSemence,
        // Actions
        hydrateStocks,
        resetStock,
        getStock,
        getStockHeterospermie,
        getSpecificStock,
        load,
        get,
        searchStoreById,
        save,
        delete: deleteAction,
        toForm,
        newForm,
        newItem,
    };
});

signalRManager.connection.on("MajStock", async (payload: IPayloadStore) => {
    const store = useStockStore();
    switch (payload.action) {
        case "Create": {
            if (store.datas.findIndex((a) => a.id == payload.data?.id) == -1) {
                const [hydrated] = await store.hydrateStocks([payload.data as IStockApiResponse]);
                store.datas.push(hydrated);
            }
            break;
        }
        case "Update": {
            const idx = store.datas.findIndex((a) => a.id == payload.data?.id);
            if (idx > -1) {
                const [hydrated] = await store.hydrateStocks([payload.data as IStockApiResponse]);
                store.datas.splice(idx, 1, hydrated);
            }
            break;
        }
        case "Delete": {
            const idx = store.datas.findIndex((a) => a.id == payload.id);
            if (idx > -1) store.datas.splice(idx, 1);
            break;
        }
    }
});
